import {
  Box,
  Collapse,
  Flex,
  IconButton,
  ListItem,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Divider } from '@time-line/components/common';
import { Delete } from '@time-line/components/icons';
import { useCartUI } from 'hooks/cart/useCartUI';
import { useRemoveItem } from 'hooks/cart/useRemoveItem';
import { useUpdateItem } from 'hooks/cart/useUpdateItem';
import { useShopifyProduct } from 'hooks/product/useShopifyProduct';
import { useAnalytics } from 'hooks/useAnalytics';
import Img from 'next/image';
import type { CartLineUpdateInput } from 'services/shopify';
import type { CartLineItem as CartLineItemType } from 'types/cart/CartLineItem';

import { CartItemStepper } from '../CartItemStepper';
import { CartProductShippingInfo } from '../CartProductShippingInfo';
import { UpsellSubscriptionCard } from '../UpsellSubscriptionCard';
import { CartLineItemCost } from './CartLineItemCost';
import { CartLineItemDropdown } from './CartLineItemDropdown';
import { CartLineItemInfoBullets } from './CartLineItemInfoBullets';
import { CartLineItemTitle } from './CartLineItemTitle';
import { usePurchaseTypeConfig } from './usePurchaseTypeConfig';

interface CartItemProps {
  lineItem: CartLineItemType;
  showDivider?: boolean;
  isInfoBulletsVisible?: boolean;
}

export function CartLineItem({
  lineItem,
  showDivider = true,
  isInfoBulletsVisible = false,
}: CartItemProps) {
  const queryClient = useQueryClient();
  const { analytics } = useAnalytics();

  const { addError, errors } = useCartUI();
  const relatedError = errors.find((item) => item.id === lineItem.id);

  const {
    productTitle,
    subtitle,
    selectorSubtitle,
    isPreorder,
    isSubscription,
    isMoneyBackGuarantee,
    upsellOptions,
    id: lineItemId,
    handle,
    cost,
    quantity,
    image,
    maxQuantity,
  } = usePurchaseTypeConfig(lineItem);

  const { data: shopifyProduct } = useShopifyProduct(handle);

  const { mutate: removeItem, isPending: isRemoving } =
    useRemoveItem(queryClient);
  const { mutate: updateItem, isPending: isUpdating } =
    useUpdateItem(queryClient);

  const handleUpdateItem = ({
    merchandiseId,
    sellingPlanId,
    attributes,
  }: Omit<CartLineUpdateInput, 'id'>) => {
    updateItem(
      {
        id: lineItem.id,
        merchandiseId,
        sellingPlanId,
        attributes,
      },
      {
        onError: () => {
          addError({
            id: lineItem.id,
            message: 'Unable to update cart item',
            errorRemovalTimeout: 6000,
          });
        },
      },
    );
  };

  const handleRemoveItem = () => {
    if (shopifyProduct) {
      analytics.removeFromCart([
        {
          product: shopifyProduct,
          merchandiseId: lineItem.merchandise.id,
          quantity: lineItem.quantity,
        },
      ]);
    }

    removeItem(lineItemId);
  };

  return (
    <ListItem
      opacity={isRemoving || isUpdating ? 0.5 : undefined}
      pointerEvents={isRemoving || isUpdating ? 'none' : undefined}
    >
      {showDivider && <Divider mb="2xs" />}
      <Stack spacing="sm">
        <Stack
          direction="row"
          alignItems="stretch"
          spacing={{ base: 'xs', md: 'sm' }}
        >
          {image?.url && (
            <Box
              width="25%"
              overflow="hidden"
              borderRadius="base"
              position="relative"
              alignSelf="start"
              maxW="175px"
            >
              <Img
                alt={productTitle}
                height={175}
                width={175}
                src={image.url}
                sizes="175px"
              />
            </Box>
          )}
          <Flex flexDir="column" justifyContent="space-between" flexGrow={1}>
            <Box>
              <Flex justifyContent="space-between">
                <Box mb="3xs">
                  <CartLineItemTitle
                    title={productTitle}
                    subtitle={subtitle}
                    isPreorder={isPreorder}
                  />
                  {isSubscription && (
                    <CartLineItemDropdown
                      lineItem={lineItem}
                      onUpdateItem={handleUpdateItem}
                    />
                  )}
                  <Text
                    data-testid="delivery-frequency"
                    size="detail"
                    color="blackAlpha.700"
                    mt={isSubscription ? '4xs' : '3xs'}
                  >
                    {selectorSubtitle}
                  </Text>
                </Box>
                <CartLineItemCost
                  cost={cost}
                  discountAllocations={lineItem.discountAllocations}
                />
                <Box display={{ md: 'none' }}>
                  <IconButton
                    aria-label="Remove item"
                    variant="link"
                    sx={{
                      svg: {
                        boxSize: 'sm',
                      },
                    }}
                    icon={
                      <Delete
                        boxSize={{ base: 'sm', md: 'xs' }}
                        sx={{
                          path: {
                            vectorEffect: 'non-scaling-stroke',
                          },
                        }}
                      />
                    }
                    onClick={() => handleRemoveItem()}
                  />
                </Box>
              </Flex>

              {isInfoBulletsVisible && (
                <CartLineItemInfoBullets
                  isMoneyBackGuarantee={isMoneyBackGuarantee}
                  isSubscription={isSubscription}
                />
              )}
              <CartLineItemCost
                cost={cost}
                discountAllocations={lineItem.discountAllocations}
                isMobile
              />
            </Box>

            <Flex justifyContent="space-between" alignItems="center">
              <Box
                display="grid"
                width={{ base: '100%', md: 180 }}
                pos="relative"
                data-testid="line-item-total"
              >
                <CartItemStepper
                  id={lineItemId}
                  merchandiseId={lineItem.merchandise.id}
                  quantity={quantity}
                  maxQuantity={maxQuantity || undefined}
                />
              </Box>
              <Box display={{ base: 'none', md: 'block' }}>
                <Button
                  onClick={() => handleRemoveItem()}
                  variant="link"
                  textDecoration="none"
                  _hover={{ textDecoration: 'underline' }}
                  leftIcon={<Delete vectorEffect="non-scaling-stroke" />}
                  sx={{
                    svg: {
                      boxSize: 'xs',
                    },
                  }}
                  isLoading={isRemoving}
                >
                  Remove
                </Button>
              </Box>
            </Flex>
          </Flex>
        </Stack>
        <Collapse in={!!relatedError} animateOpacity>
          <Box mt="2xs">
            <Alert status="error">{relatedError?.message}</Alert>
          </Box>
        </Collapse>
        <CartProductShippingInfo productId={lineItem.merchandise.product.id} />
        {upsellOptions.length && (
          <Box
            py="2xs"
            borderRadius="base"
            px="sm"
            bgColor="gray.100"
            sx={{
              containerType: 'inline-size',
            }}
          >
            {upsellOptions.map((subscription, subIndex) => (
              <>
                <UpsellSubscriptionCard
                  key={subIndex}
                  onUpdateItem={handleUpdateItem}
                  subscription={subscription}
                  lineItem={lineItem}
                  isUpdatingItem={isUpdating}
                />
                {subIndex !== upsellOptions.length - 1 && (
                  <Divider my="2xs" addOpacity />
                )}
              </>
            ))}
          </Box>
        )}

        {/* TODO: Upsells could be undone by adding the item in */}
        {/* lineItem.preUpsellLineItem to the cart, then removing the current item */}

        {/* {lineItem.preUpsellLineItem && ( */}
        {/*   <Alert status="info" sx={{ svg: { _first: { display: 'none' } } }}> */}
        {/*     You've upgraded to a 1-month supply delivered monthly.{' '} */}
        {/*     <chakra.button textDecoration="underline">Undo</chakra.button> */}
        {/*   </Alert> */}
        {/* )} */}
      </Stack>
    </ListItem>
  );
}
